import { animate, query, stagger, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { MaterialText } from '@core/models';
import { GamesAudio } from '@core/services/games';
import { DimensionsListenerDirective } from '@games/shared/game-dynamic-blocks/dimensions-listener.directive';
import { TranslateModule } from '@ngx-translate/core';
import { FitTextModule } from '@shared/components/games';
import { random } from 'lodash';
import { TextContainerComponent } from '../text-container/text-container.component';
import { TextsQuizService } from './texts-quiz.service';
import { SpeedReadingGameServiceService } from '@pages/games/game/services/game-specific/speed-reading-game-service.service';
import { GAME_SYNCHRONIZER_TOKEN, SyncClass, ISynchronizer, SyncMethod } from '@games/core/sync';
import { GameAudio } from '@games/core/controller/component-blocks/audio';
import { TextTransformationService } from '../../text-transformation.service';

export interface TextQuizQuestion {
  question: string;
  options: QuestionOption[];
  mode?: 'single' | 'multiple';
}

interface QuestionOption {
  isCorrect: boolean;
  text: string;
}

interface QuestionState {
  state: State;
  answerNumber: number;
  correctAnswers: number;
}

export interface QuizFinishState {
  answers: Answer[];
  answerNumber: number;
  correctAnswers: number;
}

interface QuestionOptions {
  answerOnSelect: boolean;
  shuffleOptions: boolean;
  canSubmitEmpty: boolean;
  showOptionsImmediately: boolean;
  showResult: boolean;
  autostart: boolean;
}

type State = 'idle' | 'question' | 'options' | 'questionTransition' | 'result';

const QUESTION_TRANSITION_TIME = 700;

interface Answer {
  option: string;
  isCorrect: boolean;
}

@Component({
  selector: 'ms-texts-quiz',
  imports: [
    CommonModule,
    TranslateModule,
    FitTextModule,
    TextContainerComponent,
    DimensionsListenerDirective,
  ],
  templateUrl: './texts-quiz.component.html',
  styleUrls: ['./texts-quiz.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state(
        'void',
        style({
          opacity: 0,
        }),
      ),
      transition(':enter', [animate('1s 1s ease-out')]),
    ]),
    trigger('optionFadeIn', [
      transition(':enter', [
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'translateX(-20px)' }),
            stagger('70ms', [
              animate('0.3s ease-out', style({ opacity: 1, transform: 'translateX(0px)' })),
            ]),
          ],
          { optional: true },
        ),
      ]),
    ]),
  ],
  hostDirectives: [DimensionsListenerDirective],
})
@SyncClass()
export class TextsQuizComponent {
  private readonly _gameSpeedReadingService = inject(SpeedReadingGameServiceService);
  private readonly _textTransformationService = inject(TextTransformationService);

  answers: Answer[] = [];

  answerNumber: number = 0;

  correctAnswers: number = 0;

  get showOptionsManually() {
    return this._gameSpeedReadingService.showOptionsManually;
  }

  gameAudio = inject(GameAudio);

  // @Input() gamesAudio: GamesAudio;

  @Input('synchronizer') __synchronizer: any;

  synchronizer: ISynchronizer = inject(GAME_SYNCHRONIZER_TOKEN);

  @Input()
  public get questions(): TextQuizQuestion[] {
    return this._questions;
  }

  public set questions(value: TextQuizQuestion[]) {
    this._questions = value;
  }

  @Input()
  public set text(value: MaterialText) {
    this.questions = value.questions.map((questionRaw) => {
      const options = [
        ...questionRaw.options.map((optionRaw) => ({
          text: optionRaw.option,
          isCorrect: questionRaw.answer === optionRaw.option,
        })),
      ];

      const correctOptions = options.filter((option) => option.isCorrect);

      if (correctOptions.length === 0) {
        options.push({
          isCorrect: true,
          text: questionRaw.answer,
        });
      }

      return {
        question: questionRaw.question,
        options,
      };
    });
  }

  private _questions: TextQuizQuestion[];

  @Input() options: QuestionOptions = {
    answerOnSelect: true,
    shuffleOptions: true,
    canSubmitEmpty: false,
    showOptionsImmediately: false,
    showResult: false,
    autostart: true,
  };

  @Output() changeState = new EventEmitter<QuestionState>();

  @Output('finish') finishEventEmitter = new EventEmitter<QuizFinishState>();

  activeQuestion: TextQuizQuestion | null = null;

  activeQuestionIndex: number | null = null;

  state: State = 'idle';

  selectedOption: QuestionOption | null = null;

  get canSubmit() {
    return this.selectedOption || this.options.canSubmitEmpty;
  }

  get isQuestioning() {
    return ['question', 'options', 'result'].includes(this.state);
  }

  questionWrapperStyle: Partial<CSSStyleDeclaration> = {};

  delimiter: Partial<CSSStyleDeclaration> = {};

  optionContainerStyle: Partial<CSSStyleDeclaration> = {};

  optionStyle: Partial<CSSStyleDeclaration> = {};

  transitionContent: string = '';

  outerStyles: Partial<CSSStyleDeclaration> = {};

  constructor(
    private readonly _textsQuizService: TextsQuizService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _elementRef: ElementRef,
    private readonly _dimensionsListener: DimensionsListenerDirective,
  ) {
    this._dimensionsListener.sizeChanges.subscribe((res) => {
      this._adjust(res.width, res.height);
    });
  }

  ngOnInit() {
    if (this.options.shuffleOptions) {
      this.shuffleOptions();
    }

    if (this.options.autostart) {
      this.setNextQuestion();
    }
  }

  // TODO V3: Из-за того, что этот метод вызывается в ngOnInit, его вызов дублируется.
  shuffleOptions() {
    // Используем детерминированное перемешивание для синхронизации между клиентами
    this.questions.forEach((question, questionIndex) => {
      // Создаем детерминированный seed на основе индекса вопроса
      const seed = questionIndex * 137; // Простое число для лучшей распределенности
      question.options = this._textTransformationService.deterministicShuffle(question.options, seed);
    });
    this.setQuestions(this.questions);
  }

  @SyncMethod() // TODO V3: Из-за того, что этот метод вызывается в ngOnInit, его вызов дублируется.
  setQuestions(questions: TextQuizQuestion[]) {
    this.questions = questions;
  }

  setNextQuestion() {
    if (this.activeQuestion === null) {
      this.activeQuestionIndex = 0;
      this.selectedOption = null;
      this.answers = [];
      this.correctAnswers = 0;
    } else {
      this.activeQuestionIndex++;
    }
    this.showQuestionTransition();
  }

  showQuestionTransition() {
    this.state = 'questionTransition';
    this.transitionContent = this.selectedOption?.text || 'Вопросы';
    this._cdr.markForCheck();

    // todo: change setTimeout
    this._textsQuizService.playVhsClick(this.gameAudio);
    setTimeout(() => {
      this.showQuestion();
      this._cdr.markForCheck();
    }, QUESTION_TRANSITION_TIME);
  }

  showQuestion() {
    if (this.activeQuestionIndex < this.questions.length) {
      this.activeQuestion = this.questions[this.activeQuestionIndex];
      this.selectedOption = null;
      if (this.showOptionsManually) {
        this.state = this.options.showOptionsImmediately ? 'options' : 'question';
      } else {
        this.state = 'question';
        const WORDS_IN_MINUTES = 80;
        const TIME_FOR_READING =
          ((this.activeQuestion.question.split(' ').length * 60) / WORDS_IN_MINUTES) * 1000;
        setTimeout(() => {
          this.state = 'options';
          this._cdr.markForCheck();
          this._emitState();
        }, TIME_FOR_READING);
      }
      this._emitState();
    } else {
      // finish
      this.activeQuestionIndex = null;
      this.activeQuestion = null;
      this._emitState();
      this.state = 'idle';
      this.finishEventEmitter.next({
        answers: this.answers,
        answerNumber: this.answerNumber,
        correctAnswers: this.correctAnswers,
      });
    }
  }

  @SyncMethod()
  onQuestionClick() {
    if (this.state === 'question' && this.showOptionsManually) {
      this.state = 'options';
      this._cdr.markForCheck();
    }
  }

  showOptions() {
    this.state = 'options';
  }

  @SyncMethod()
  selectOption(optionIndex: number) {
    this.selectedOption = this.activeQuestion.options[optionIndex];
    if (this.options.answerOnSelect) {
      this.submitAnswer();
    }
  }

  submitAnswer() {
    if (!this.canSubmit) {
      throw new Error('Невозможно подтвердить ответ.');
    }

    this.answerNumber++;

    this.answers.push({
      option: this.selectedOption.text,
      isCorrect: this.selectedOption.isCorrect,
    });

    if (!this.selectedOption) {
    } else if (this.selectedOption.isCorrect) {
      this.correctAnswers++;
    } else {
    }
    this._emitState();

    if (this.options.showResult) {
      this.state = 'result';
    } else {
      this.setNextQuestion();
    }
  }

  private _adjust(width: number, height: number) {
    this.outerStyles = {
      fontSize: Math.min(height, width) * 0.001 + 'px',
    };
  }

  private _emitState() {
    this.changeState.next({
      state: this.state,
      answerNumber: this.answerNumber,
      correctAnswers: this.correctAnswers,
    });
  }
}
